import { LegalLayout, LegalTable } from "../components/site/LegalLayout";

const sections = [
  {
    id: "collect",
    number: "01",
    title: "Information we collect",
    content: (
      <>
        <p><strong>Information you provide directly.</strong></p>
        <LegalTable
          head={["Category", "Examples", "Why"]}
          rows={[
            ["Account & identity", "Name, email, hashed password, role (professional / owner / admin)", "Authentication, account creation"],
            ["License verification", "License number, issuing state, license photo", "Verifying professional credentials before applications can be submitted"],
            ["Profile", "Bio, specialty (hair, nails, lash, esthetics, etc.), years of experience, profile photo, portfolio photos", "Display on profile, listing applications"],
            ["Listings (owners)", "Business name, description, address, photos, compensation type, salary range", "Marketplace functionality — these are public-facing posts"],
            ["Applications", "Application message, listing the application is for", "Routing applications to the right owner"],
            ["Messages", "Sender, recipient, message content, timestamp, read status", "In-app chat feature"],
            ["Payments", "Transaction ID and product purchased (for Listing Boosts and Verified Pro subscriptions)", "Granting purchased entitlements + receipt audit trail. Card numbers are never seen by us — handled entirely by Apple In-App Purchase / Google Play Billing."],
          ]}
        />
        <p><strong>Information collected automatically.</strong></p>
        <LegalTable
          head={["Category", "Examples", "Why"]}
          rows={[
            ["Device push token", "Expo / Apple Push Notification Service / Firebase Cloud Messaging device token", "Deliver push notifications (new applications, messages, license decisions, etc.)"],
            ["Precise & coarse location", "Latitude/longitude (only when you tap “Use my location”), city, state, postal code", "Map-based radius search and showing nearby listings. We do not collect background location."],
            ["Server logs", "API endpoint hit, HTTP status, request timing, IP address (truncated)", "Operational debugging and abuse prevention. Logs are retained 30 days then rotated."],
          ]}
        />
        <p><strong>What we do not collect.</strong></p>
        <ul>
          <li>We do not collect health, fitness, sensitive (racial / ethnic / religious / political), biometric, contacts, browsing history, advertising identifiers, or audio data.</li>
          <li>We do not use third-party analytics SDKs (no Mixpanel, Amplitude, Segment, Firebase Analytics, etc.).</li>
          <li>We do not have ad networks embedded in the App.</li>
          <li>We do not track you across other apps or websites (no IDFA / advertising-ID collection).</li>
        </ul>
      </>
    ),
  },
  {
    id: "use",
    number: "02",
    title: "How we use your information",
    content: (
      <>
        <ul>
          <li><strong>To provide the service</strong> — authenticate you, deliver messages, route applications, render listings, verify licenses, process IAP purchases, send push notifications.</li>
          <li><strong>To personalize your experience</strong> — greet you by first name, surface nearby listings, show your unread badge, display the Verified Pro / Boosted indicators based on your purchase history.</li>
          <li><strong>To prevent fraud and abuse</strong> — verify professional licenses; verify IAP receipts with Apple/Google; rate-limit suspicious activity.</li>
          <li><strong>To comply with law</strong> — respond to lawful requests, enforce our Terms of Service.</li>
        </ul>
        <p>We do not use your data for advertising or sell it to data brokers.</p>
      </>
    ),
  },
  {
    id: "share",
    number: "03",
    title: "How we share your information",
    content: (
      <>
        <p>We share your information only with the following categories of third parties, and only as necessary to operate the App:</p>
        <LegalTable
          head={["Third party", "What is shared", "Why"]}
          rows={[
            ["Apple (App Store, IAP, APNs)", "Device push token, IAP receipt data", "Push notifications, in-app purchase processing"],
            ["Google (Play Store, Play Billing, FCM)", "Device push token, Play purchase token", "Push notifications, in-app purchase processing on Android"],
            ["Expo (push relay service)", "Expo push token", "Routing push notifications to APNs/FCM"],
            ["MongoDB Atlas / our backend", "All account & content data", "Database hosting (encrypted at rest & in transit)"],
            ["Other ProBeauty Connect users", "Your public profile, listings, applications, messages you send", "The marketplace itself — you control what you post"],
          ]}
        />
        <p>We may also disclose information if required by law, valid legal process, or to protect rights, safety, or property.</p>
      </>
    ),
  },
  {
    id: "retention",
    number: "04",
    title: "Data retention",
    content: (
      <>
        <ul>
          <li><strong>Account & profile data:</strong> retained while your account is active.</li>
          <li><strong>Messages:</strong> retained for the lifetime of the conversation, deletable on request.</li>
          <li><strong>Server logs:</strong> 30 days.</li>
          <li><strong>IAP transaction records:</strong> retained for 7 years for tax/audit compliance.</li>
          <li><strong>License verification photos:</strong> deleted within 30 days of verification or rejection.</li>
        </ul>
        <p>When you delete your account, all personal data is permanently removed within 30 days, except records we are legally required to keep (e.g., financial transactions for tax purposes, which are anonymized).</p>
      </>
    ),
  },
  {
    id: "security",
    number: "05",
    title: "Security",
    content: (
      <ul>
        <li>Passwords are stored using bcrypt hashing — we never see your password in plaintext.</li>
        <li>All API traffic is encrypted via HTTPS/TLS 1.2+.</li>
        <li>Authentication uses signed JWT bearer tokens stored in the device's secure storage (iOS Keychain via expo-secure-store).</li>
        <li>Database encryption at rest.</li>
        <li>Role-based access control on every API endpoint.</li>
      </ul>
    ),
  },
  {
    id: "rights",
    number: "06",
    title: "Your rights",
    content: (
      <>
        <p>Depending on where you live, you may have rights under the GDPR (EU/UK), CCPA/CPRA (California), or other laws. These include:</p>
        <ul>
          <li><strong>Right to access</strong> — request a copy of the data we hold about you.</li>
          <li><strong>Right to deletion</strong> — request permanent removal of your account.</li>
          <li><strong>Right to correction</strong> — fix inaccurate information via Edit Profile or by contacting us.</li>
          <li><strong>Right to data portability</strong> — receive a machine-readable export of your data.</li>
          <li><strong>Right to opt out of sale/sharing</strong> — we don't sell or share your data for advertising, so there's nothing to opt out of, but the right is preserved.</li>
        </ul>
        <p>To exercise any of these, visit <a href="/privacy-choices">Your Privacy Choices</a> or email <a href="mailto:privacy@probeauty.app">privacy@probeauty.app</a>. We respond within 30 days.</p>
      </>
    ),
  },
  {
    id: "children",
    number: "07",
    title: "Children",
    content: (
      <p>ProBeauty Connect is not directed to children under 16. We do not knowingly collect data from children under 16. If you become aware that a child has provided data, please contact us at <a href="mailto:privacy@probeauty.app">privacy@probeauty.app</a> and we will delete it promptly.</p>
    ),
  },
  {
    id: "international",
    number: "08",
    title: "International transfers",
    content: (
      <p>Data is processed in the United States. If you're accessing from outside the US, your data will be transferred to and stored in the US under standard contractual protections.</p>
    ),
  },
  {
    id: "changes",
    number: "09",
    title: "Changes to this policy",
    content: (
      <p>We may update this policy from time to time. Material changes will be communicated via in-app notification and an updated “Effective Date” at the top of this page.</p>
    ),
  },
  {
    id: "contact",
    number: "10",
    title: "Contact us",
    content: (
      <>
        <p><strong>Privacy Team</strong></p>
        <p>Email: <a href="mailto:privacy@probeauty.app">privacy@probeauty.app</a><br />Postal: ProBeauty Connect Privacy Office · United States</p>
      </>
    ),
  },
];

export default function PrivacyPolicy() {
  return (
    <LegalLayout
      eyebrow="ProBeauty Connect"
      titleLines={["Privacy", "Policy"]}
      meta="Effective Date: May 29, 2026 · Last Updated: May 29, 2026"
      intro="This Privacy Policy explains how we collect, use, store, share, and protect your personal information when you use ProBeauty Connect. By creating an account or using the App, you agree to the practices described below."
      sections={sections}
    />
  );
}
